<template>
  <b-modal
    :id="'fulfillment-tracking-modal'"
    centered
    hide-footer
    hide-header
    size="xl"
    @show="getTrackingData()"
    @hide="trackingModalClosed()"
  >
    <div class="tracking-info">
      <div v-if="loading">
        <ProcessingGif :processing="loading" />
        <div v-if="isPolling">
          <div class="centered-block">
            <div class="step-title">This is taking a bit longer. Please wait while we fetch your tracking information...</div>
          </div>
        </div>
      </div>
      <div v-else-if="tracking">
        <div class="centered-block">
          <div class="step-title">Tracking Information</div>
        </div>
        <div class="summary">
          <div><label>Carrier:</label> {{tracking.carrier}}</div>
          <div><label>Status:</label> {{getStatusText(tracking.status)}}</div>
          <div v-if="tracking.est_delivery_date && tracking.est_delivery_date.length">
            <label>Est. Delivery Date:</label>
            {{estimatedDeliveryDate}}
          </div>
        </div>
        <b-table striped hover :items="details" :fields="detailFields" class="tracking-table">
          <template v-slot:cell(date)="data">
            <div>{{data.value}}</div>
            <div class="time">{{data.item.time}}</div>
          </template>
        </b-table>
      </div>
      <div v-else-if="!fulfillment">
        <div class="centered-block">
          <div class="step-title">We are waiting for the tracking information to scan.</div>
        </div>
      </div>
      <div v-else>
        <div class="centered-block">
          <div class="step-title">Invalid tracking ID or couldn't find any data</div>
        </div>
      </div>
    </div>
    <b-link block class="centered-block close-button" variant="secondary" @click="close()">&times;</b-link>
  </b-modal>
</template>

<script>
import { TrackingStatusStrings } from '@/constants'
import { getShortTime, getShortDate } from '@/utils'
import axios from 'axios'
import ProcessingGif from '@/components/ProcessingGif'

export default {
  name: 'Tracking',
  components: {ProcessingGif},
  props: {
    fulfillment: {
      type: Object,
      default: null
    },
    nextInput: {
      type: Number,
      default: null
    }
  },
  data: function() {
    return {
      loading: false,
      tracking: null,
      detailFields: [
        {key: 'date'},
        {key: 'location'},
        {key: 'status'},
        {key: 'message'}
      ],
      pollingAttempts: 0,
      maxPollingAttempts: 3,
      pollingInterval: null,
      isPolling: false
    }
  },
  computed: {
    details: function() {
      let details = []

      for (let detail of this.tracking.details) {
        let location = ''
        let country = detail.country || 'US'

        if (detail.city && detail.state) {
          location = detail.city + ', ' + detail.state + ', ' + country
        }

        details.push({
          date: getShortDate(detail.scan_date),
          time: getShortTime(detail.scan_date),
          location: location,
          status: this.getStatusText(detail.status),
          message: detail.message
        })
      }

      return details
    },
    estimatedDeliveryDate: function() {
      return getShortDate(this.tracking.est_delivery_date)
    }
  },
  methods: {
    getStatusText: function(status) {
      return TrackingStatusStrings[status]
    },
    close: function() {
      this.$bvModal.hide('fulfillment-tracking-modal')
    },
    getTrackingData() {
      if (!this.tracking && this.fulfillment) {
        this.loading = true
        this.pollingAttempts = 0
        this.isPolling = false

        // Initial fetch
        this.fetchTrackingInfo()
      }
    },
    fetchTrackingInfo() {
      axios.get('/fulfillments/' + this.fulfillment.id.toString() + '/').then(result => {
        console.log('Tracking fetch attempt ' + (this.pollingAttempts + 1), result)

        const trackingData = result.data.tracking

        // Check if we got valid tracking data
        if (trackingData) {
          // Success! We have tracking data
          this.tracking = trackingData
          this.loading = false
          this.stopPolling()
        } else if (this.pollingAttempts < this.maxPollingAttempts) {
          // No data yet, but we haven't exceeded max attempts
          // Start polling if not already polling
          if (!this.isPolling) {
            this.startPolling()
          }
        } else {
          // Exceeded max attempts, stop trying
          this.tracking = trackingData // Set whatever we got (might be null)
          this.loading = false
          this.stopPolling()
        }
      }, error => {
        console.error('Error fetching tracking data:', error)
        this.loading = false
        this.stopPolling()
      })
    },
    startPolling() {
      this.isPolling = true

      // Poll every 2 seconds
      this.pollingInterval = setInterval(() => {
        this.pollingAttempts++
        this.fetchTrackingInfo()
      }, 2000)
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
      this.isPolling = false
    },
    trackingModalClosed() {
      // Clean up polling when modal closes
      this.stopPolling()
      this.tracking = null
      this.pollingAttempts = 0

      if (this.nextInput) {
        this.$store.commit('busy')
        this.$store.dispatch('input', {input: this.nextInput})
          .then(_ => {
            this.$store.commit('clear_messages')
            this.$store.commit('ready')
          })
          .catch(err => {
            console.log(err)
            if (!err.response || err.response.status !== 422) {
              this.$store.commit('critical_error', err)
            }
          })
      }
    }
  },
  beforeDestroy() {
    // Clean up polling interval if component is destroyed
    this.stopPolling()
  }
}
</script>

<style scoped lang="less">
  .tracking-info {
    .summary {
      > div {
        line-height: 14px;
      }

      label {
        font-weight: bold;
        margin-right: 5px;
      }

      .tracking-table {
        .time {
          white-space: nowrap;
        }
      }
    }
  }

</style>
